import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { VideoFactoryProject } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

interface Props {
  onClose: VoidFunction;
  project: VideoFactoryProject;
}

export interface FormDataT {
  prompt: string;
}

const AnimateImagesDrawer: FC<Props> = ({ onClose, project }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      prompt: 'add motion',
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        const res = await api.post<typeof data, { error?: string }>(
          `/v1/video-factory/projects/${project.id}/animate`,
          data,
        );

        if (res.error) {
          return alerts.error('Request Failed', res.error);
        }

        await queryClient.invalidateQueries(['video_factory_projects']);

        onClose();

        alerts.success('Task successfully started');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Create animated video" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                label="Prompt"
                control={control}
                name="prompt"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default AnimateImagesDrawer;
